import { default as classNames, default as cx } from "classnames";
import authContext from "jwt_context&axios/authContext";
import MarkRepliedUnreplied from "components/chat/MarkRepliedUnreplied";
import { SelectedChannelsContext } from "components/chat/SelectedChannelContext";
import { Checkbox } from "components/DesignSystem/Checkbox/Checkbox";
import { HighlightSearchTerm } from "components/DesignSystem/HighlightText";
import { DynamicStar } from "components/DesignSystem/Star/DynamicStar";
import { ArchivedChat } from "components/icons/ArchivedChat";
import { ArchivedChatGeneric } from "components/icons/ArchivedChatGeneric";
import { ArchivedChatInactive } from "components/icons/ArchivedChatInactive";
import { BooksChat } from "components/icons/BooksChat";
import { BooksChatActive } from "components/icons/BooksChatActive";
import { BooksChatGeneric } from "components/icons/BooksChatGeneric";
import { Pin } from "components/icons/Chat/Pin";
import { DeletedMessage } from "components/icons/DeletedMessage";
import { DocIcon } from "components/icons/DocIcon";
import { Galary } from "components/icons/Galary";
import { GeneralHelp } from "components/icons/GeneralHelp";
import { GeneralHelpGeneric } from "components/icons/GeneralHelpGeneric";
import { GeneralHelpIncative } from "components/icons/GeneralHelpInactive";
import { NewAttachments } from "components/icons/NewAttachmens";
import { PrivateChat } from "components/icons/PrivateChat";
import { PrivateChatGeneric } from "components/icons/PrivateChatGeneric";
import { PrivateChatInactive } from "components/icons/PrivateChatInactive";
import { Reply } from "components/icons/Reply";
import { SalesTaxChat } from "components/icons/SalesTaxChat";
import { SalesTaxChatInactive } from "components/icons/SalesTaxChatInactive";
import { TaskChatActive } from "components/icons/TaskChatActive";
import { TaskChatGeneric } from "components/icons/TaskChatGeneric";
import { TaskChatInavctive } from "components/icons/TaskChatInactive";
import { Mention } from "components/MessageBubble/MessageBubble";
import { MessageDateFormat } from "components/MessageDateFormat/MessageDateFormat";
import {
  BOOKS_CHANNEL_CUSTOM_TYPE,
  PRIVATE_CHANNEL_CUSTOM_TYPE,
  SALES_TAX_CUSTOM_TYPE,
} from "constants/channelType";
import { MESSAGE_TYPE_DELETED } from "constants/chatType";
import dayjs from "dayjs";
import isToday from "dayjs/plugin/isToday";
import isYesterday from "dayjs/plugin/isYesterday";
import { useAppSelector } from "hooks/useAppSelector";
import { useCurrentGroup } from "hooks/useCurrentGroup";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useContext, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  closeMessageInfoSlider,
  closeShareViaWhatsappSlider,
} from "store/slices/channelInfoSlider";
import { setScrollToMessageId } from "store/slices/messageToScrollTo";
import { resetReviewBalancePaymentState } from "store/slices/reviewAndBalancePayment";
import { DefaultGenerics } from "stream-chat";
import {
  ChannelPreviewUIComponentProps,
  renderText,
  StreamMessage,
} from "stream-chat-react";
import { pluralize } from "utils/pluralize";
import { stopPropagation } from "utils/stopPropagation";

dayjs.extend(isYesterday);
dayjs.extend(isToday);

const MessagePreview = ({
  message,
  lastMessageDeleted,
}: {
  message?: StreamMessage<DefaultGenerics>;
  lastMessageDeleted?: boolean;
}) => {
  const isReply = Boolean(message?.quoted_message_id);
  const isAttachment =
    message && message?.attachments && message?.attachments?.length > 0;
  const isImage =
    isAttachment && message?.attachments?.at(-1)?.type === "image";

  const fallbackImage =
    (isImage && message?.attachments?.at(-1)?.fallback) || "";

  const isDoc =
    !message?.text && message?.attachments && message?.attachments?.length > 0;

  const docTitle = message?.attachments?.at(-1)?.title;

  const renderer = useMemo(
    () =>
      renderText(message?.text, message?.mentioned_users, {
        customMarkDownRenderers: {
          mention: Mention,
        },
        allowedTagNames: [],
      }),
    [message?.text, JSON.stringify(message?.mentioned_users || [])]
  );

  if (message?.forwarded_message) {
    return (
      <div className="t-flex t-gap-1.5 t-items-center t-text-neutral-40">
        <div className="w-4 t-h-4 t-flex t-items-center t-justify-center">
          <Reply />
        </div>
        <p className="t-break-all t-text-body-sm t-text-text-60 t-line-clamp-1 t-m-0">
          {isImage ? fallbackImage : isDoc ? docTitle : message?.text}
        </p>
      </div>
    );
  }

  if (message && isReply) {
    return (
      <div className="t-flex t-gap-1.5 t-items-center t-text-neutral-40">
        <div className="w-4 t-h-4 t-flex t-items-center t-justify-center t-scale-y-[-1]">
          <Reply />
        </div>
        <p className="t-break-all t-text-body-sm t-text-text-60 t-line-clamp-1 t-m-0">
          {isImage ? fallbackImage : isDoc ? docTitle : message?.text}
        </p>
      </div>
    );
  }

  if (lastMessageDeleted) {
    return (
      <div className="t-break-all t-text-body-sm t-text-text-60 t-line-clamp-1 t-italic t-flex t-items-center t-gap-1.5">
        <DeletedMessage />
        Deleted message
      </div>
    );
  }
  if (message?.text) {
    return (
      <div className=" t-break-all t-text-body-sm t-text-text-60 t-line-clamp-1">
        {renderer}
      </div>
    );
  }

  if (!message?.text && isImage) {
    return (
      <div className="t-flex t-gap-1.5  t-items-center t-text-neutral-40">
        <Galary />
        <p className="t-break-all t-text-body-sm t-text-text-60 t-line-clamp-1 t-m-0">
          {fallbackImage}
        </p>
      </div>
    );
  }

  if (isDoc) {
    return (
      <div className="t-flex t-gap-1.5 t-items-center ">
        <div className="t-flex t-items-center t-justify-center">
          <DocIcon />
        </div>
        <p className="t-break-all t-text-body-sm t-text-text-60 t-line-clamp-1 t-m-0">
          {docTitle}
        </p>
      </div>
    );
  }

  if (message?.custom_data) {
    return (
      <div className="t-flex t-gap-1.5 t-items-center ">
        <NewAttachments />
        <p className="t-break-all t-text-body-sm t-text-text-60 t-line-clamp-1 t-m-0">
          {/* @ts-ignore */}
          {message.custom_data?.header}
        </p>
      </div>
    );
  }

  return (
    <span className="t-italic t-break-all t-text-body-sm t-text-text-30 t-line-clamp-1">
      No messages yet
    </span>
  );
};

const PreviewIcons = ({
  isPinned,
  isPrivate,
  isFrozen,
  isActive,
  isBooks,
  iconColor,
  isSalesTax,
}: {
  isPinned?: boolean;
  isPrivate?: boolean;
  isFrozen?: boolean;
  isActive?: boolean;
  isBooks?: boolean;
  iconColor?: string;
  isSalesTax?: boolean;
}) => {
  if (isFrozen) {
    if (iconColor !== "t-text-neutral-70") {
      return (
        <div className={classNames(iconColor)}>
          <ArchivedChatGeneric />
        </div>
      );
    }
    if (isActive)
      return (
        <div className="-t-mt-0.5">
          <ArchivedChat />
        </div>
      );
    return (
      <div className="-t-mt-0.5">
        <ArchivedChatInactive />
      </div>
    );
  }

  if (isPinned) {
    if (iconColor !== "t-text-neutral-70") {
      return (
        <div className={classNames(iconColor)}>
          <GeneralHelpGeneric />
        </div>
      );
    }
    if (isActive)
      return (
        <div className="-t-mt-0.5">
          <GeneralHelp />
        </div>
      );
    return (
      <div className="-t-mt-0.5">
        <GeneralHelpIncative />
      </div>
    );
  }

  if (isBooks) {
    if (iconColor !== "t-text-neutral-70") {
      return (
        <div className={classNames(iconColor)}>
          <BooksChatGeneric />
        </div>
      );
    }
    if (isActive) return <BooksChatActive />;
    return <BooksChat />;
  }
  if (isSalesTax) {
    if (isActive) return <SalesTaxChat />;
    return <SalesTaxChatInactive />;
  }

  if (isPrivate) {
    if (iconColor !== "t-text-neutral-70") {
      return (
        <div className={classNames(iconColor)}>
          <PrivateChatGeneric />
        </div>
      );
    }
    if (isActive)
      return (
        <div className="-t-mt-[1px]">
          <PrivateChat />
        </div>
      );
    return (
      <div className="-t-mt-[1px]">
        <PrivateChatInactive />
      </div>
    );
  }

  if (iconColor !== "t-text-neutral-70") {
    return (
      <div className={classNames(iconColor)}>
        <TaskChatGeneric />
      </div>
    );
  }
  if (isActive) return <TaskChatActive />;
  return <TaskChatInavctive />;
};

export const ChannelPreview = (
  props: ChannelPreviewUIComponentProps<DefaultGenerics> & {
    selected?: boolean;
    unreplied?: boolean;
    searchValue?: string;
    hideChannelCheckboxSelect?: boolean;
  }
) => {
  const {
    active,
    // Avatar,
    channel,
    // className: customClassName = "",
    // displayImage,
    displayTitle,
    // latestMessage,
    onSelect: customOnSelectChannel,
    setActiveChannel,
    unread,
    watchers,
    lastMessage: propsLastMessage,
    selected,
    searchValue,
    hideChannelCheckboxSelect,
  } = props;

  const unreplied = (channel?.data?.needs_reply as boolean) || props.unreplied;

  const channelPreviewButton = useRef<HTMLButtonElement | null>(null);
  const { secondary_channel_url } = useCurrentGroup();
  const { isCustomer, isAdmin, isCpa, isUserTypeForeignCA, isForeignCA } =
    useRoleBasedView();
  const isFCA = isUserTypeForeignCA || isForeignCA;
  const [showDropdown, setShowDropdown] = useState(false);
  const dispatch = useDispatch();
  const showUnreadMentionedIcon = channel.countUnreadMentions() > 0;
  const selectionContext = useContext(SelectedChannelsContext);
  const { showAIChannels } = useAppSelector((state) => state.chatSearch);

  const { selectedChannels, setSelectedChannels } = selectionContext || {};

  const lastMessage =
    propsLastMessage || channel.state.messageSets[0].messages.at(-1);

  const timestampKey = isCustomer
    ? "last_message_at"
    : "last_non_broadcast_message_at";

  const messageCreatedAt = channel?.data?.[timestampKey] as string | undefined;
  const channelCreatedAt = channel?.data?.created_at;

  const lastMessageDeleted = lastMessage?.type === MESSAGE_TYPE_DELETED;

  const onSelectChannel = (e: React.MouseEvent<HTMLButtonElement>) => {
    dispatch(setScrollToMessageId());
    dispatch(resetReviewBalancePaymentState());
    dispatch(closeShareViaWhatsappSlider());
    dispatch(closeMessageInfoSlider());
    if (customOnSelectChannel) {
      customOnSelectChannel(e);
    }
    if (setActiveChannel) {
      setActiveChannel(channel, watchers);
    }
    if (channelPreviewButton?.current) {
      channelPreviewButton.current.blur();
    }
  };

  const isChannelOpen = active || selected;
  const isPinned = secondary_channel_url === channel.id;
  const { authtoken } = useContext(authContext);
  const isFCATeamChannel = channel.id === authtoken?.service_team_channel_url;
  const showPinBadge = isPinned || (isFCA && isFCATeamChannel);
  const isPrivate = channel?.data?.custom_type === PRIVATE_CHANNEL_CUSTOM_TYPE;
  const isBooks = channel?.data?.custom_type === BOOKS_CHANNEL_CUSTOM_TYPE;
  const isSalesTax = channel?.data?.custom_type === SALES_TAX_CUSTOM_TYPE;

  const entityName = channel?.data?.entity_name as string | undefined;
  const groupName = channel?.data?.group_name as string;
  const season = channel?.data?.season as string;
  const pendingActionCount =
    (channel?.data?.pending_action_items_count as number) || 0;

  const showEntityName = Boolean(entityName);
  const hasPendingActions = pendingActionCount > 0;
  const showGroupName = !isCustomer && Boolean(groupName);
  const hasTextBeforeSeason = showGroupName || showEntityName;
  const hasTextBeforePendingActions = hasTextBeforeSeason || Boolean(season);
  let iconColor = "t-text-neutral-70";

  let unrepliedDateDiffColor = {
    componentClassName: classNames({
      "!t-bg-surface-transparent hover:!t-bg-surface-lighter-grey":
        !isChannelOpen,
    }),
    /* @tw */
    logoClassName: isPrivate
      ? "t-text-neutral-70 t-bg-surface-grey"
      : "t-bg-purple-10 t-text-purple",
  };

  const currentDate = dayjs();
  const channelData = props.channel?.data;
  const initialCustomerMessageAt = dayjs(
    // @ts-ignore
    channelData?.initial_customer_message_at
  );
  // @ts-ignore
  const lastReplied = dayjs(channelData?.last_message_at);

  const dateToCompare =
    channelData && "initial_customer_message_at" in channelData
      ? initialCustomerMessageAt
      : lastReplied;

  const daysPassed = currentDate.diff(dateToCompare, "days");

  if (daysPassed >= 3 && isAdmin && !showAIChannels) {
    unrepliedDateDiffColor = {
      componentClassName: classNames({
        "!t-bg-red-20 hover:!t-bg-red-30": !isChannelOpen,
      }),
      logoClassName: classNames("t-bg-red-30 t-text-red", {
        "group-hover:t-bg-red-40": !isChannelOpen,
      }),
    };
    iconColor = !isChannelOpen
      ? "t-text-red-40 -t-mt-[1px]"
      : "t-text-neutral-70";
  }
  if (daysPassed >= 1 && daysPassed < 3 && isAdmin && !showAIChannels) {
    unrepliedDateDiffColor = {
      componentClassName: classNames({
        "!t-bg-orange-20 hover:!t-bg-orange-30": !isChannelOpen,
      }),
      logoClassName: classNames("t-bg-orange-30 t-text-orange", {
        "group-hover:t-bg-orange-40": !isChannelOpen,
      }),
    };
    iconColor = !isChannelOpen
      ? "t-text-orange-50 -t-mt-[1px]"
      : "t-text-neutral-70";
  }

  const isSelected = selectedChannels.includes(channel.id!);

  const onChannelCheck = () =>
    setSelectedChannels((chnls) => {
      if (channel.id) {
        if (chnls.includes(channel.id)) {
          return chnls.filter((ch) => ch !== channel.id);
        } else {
          return [...chnls, channel.id];
        }
      }
      return chnls;
    });

  const showLessHeight =
    isCustomer && !showEntityName && !season && !hasPendingActions;

  return (
    <button
      aria-label={`Select Channel: ${displayTitle || ""}`}
      aria-selected={isChannelOpen}
      className={cx(
        `all:unset t-relative t-box-border t-flex t-w-full t-justify-center t-gap-2 t-p-4 t-overflow-anchor-none t-items-start t-group`,
        {
          "t-bg-surface-purple": isChannelOpen,
          "t-bg-white hover:t-bg-surface-lighter-grey": !isChannelOpen,
          "t-border-solid t-border-surface-transparent t-border-b-surface":
            unreplied,
          [unrepliedDateDiffColor.componentClassName]:
            channel?.data?.needs_reply,
          "t-group/disabled": channel.data?.frozen,
          "t-group": !channel.data?.frozen,
          "t-h-[70px]": showLessHeight,
          "t-h-[89px]": !showLessHeight,
          "t-pl-2": isAdmin || isCpa,
          // @ts-ignore
        }
      )}
      onClick={onSelectChannel}
      ref={channelPreviewButton}
      role="option"
    >
      {isChannelOpen && (
        <div className="t-absolute t-left-0 t-top-0 t-h-full t-border-0 t-border-l-4 t-border-solid t-border-purple-40" />
      )}
      {!hideChannelCheckboxSelect && (isAdmin || isCpa) && (
        <div
          className={classNames("group-hover:t-opacity-100 t-mt-[3px]", {
            "t-opacity-0": !isSelected,
            "t-opacity-100": isSelected,
          })}
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            onChange={onChannelCheck}
            checked={Boolean(
              channel.id && selectedChannels.includes(channel.id)
            )}
          />
        </div>
      )}
      <div className="t-pr-2">
        <PreviewIcons
          isPinned={isPinned}
          isPrivate={isPrivate}
          isFrozen={channel.data?.frozen as boolean}
          isActive={isChannelOpen}
          isBooks={isBooks}
          iconColor={iconColor}
          isSalesTax={isSalesTax}
        />
      </div>
      <div className="t-flex t-flex-col t-leading-[normal] t-w-full t-min-w-[50%] -t-mt-0.5">
        <div className="t-flex t-justify-between">
          <div className="t-text-subtext-sm t-line-clamp-1">
            {(displayTitle || channel.data?.name) && (
              <HighlightSearchTerm
                text={displayTitle || channel.data?.name!}
                searchTerm={searchValue || ""}
              />
            )}
          </div>
        </div>
        <div className={classNames("t-flex t-items-center")}>
          <>
            {isAdmin && channel?.data?.is_vip && (
              <div className="t-mr-1 t-h-full t-flex t-items-center t-justify-center">
                <DynamicStar isActive />
              </div>
            )}
            <>
              <div className="t-flex t-gap-1 t-items-center t-text-text-30 t-text-body-sm">
                {showGroupName && (
                  <span className="t-overflow-x-hidden t-text-ellipsis t-whitespace-nowrap">
                    <HighlightSearchTerm
                      searchTerm={searchValue || ""}
                      text={groupName}
                    />
                  </span>
                )}
                {showEntityName && (
                  <>
                    {showGroupName && (
                      <span className="t-h-0.5 t-w-0.5 t-shrink-0 t-rounded-full t-bg-text-30"></span>
                    )}
                    <span className="t-inline-block t-overflow-x-hidden t-text-ellipsis t-whitespace-nowrap">
                      {entityName}
                    </span>
                  </>
                )}
                {Boolean(season) && (
                  <>
                    {hasTextBeforeSeason && (
                      <span className="t-h-0.5 t-w-0.5 t-shrink-0 t-rounded-full t-bg-text-30"></span>
                    )}
                    <span className="t-whitespace-nowrap">{season}</span>
                  </>
                )}
                {hasPendingActions && (
                  <>
                    {hasTextBeforePendingActions && (
                      <span className="t-h-0.5 t-w-0.5 t-shrink-0 t-rounded-full t-bg-text-30"></span>
                    )}
                    <span className="t-whitespace-nowrap t-text-orange-70">
                      {pluralize(
                        pendingActionCount,
                        "action pending",
                        "actions pending"
                      )}
                    </span>
                  </>
                )}
              </div>
            </>
          </>
        </div>
        <div
          className={classNames("", {
            "t-mt-1": !showLessHeight,
            "t-mt-0": showLessHeight,
          })}
        >
          <MessagePreview
            message={lastMessage}
            lastMessageDeleted={lastMessageDeleted}
          />
        </div>
      </div>
      <div className="t-ml-auto t-flex t-flex-col t-flex-shrink-0 t-items-center t-justify-between t-h-full t-gap-1.5 t-relative t-z-10">
        <div className="t-right-6 t-text-caption t-text-neutral-40 t-self-end">
          <MessageDateFormat
            createdAt={messageCreatedAt || (channelCreatedAt as string)}
          />
        </div>
        <div
          className={classNames("t-flex t-gap-1.5 t-w-full t-justify-end", {
            "-t-mb-1": isCustomer,
            "-t-mb-1.5": !isCustomer,
          })}
        >
          {Boolean(showUnreadMentionedIcon) && (
            <div className="t-text-purple t-text-subtext-sm">@</div>
          )}
          {!!unread && (
            <div
              data-testid="unread-badge"
              className="t-flex t-h-5 t-min-w-5 t-items-center t-justify-center t-self-center t-rounded-full t-bg-purple-50 t-px-1 t-text-body-sm t-text-white"
            >
              {unread}
            </div>
          )}
          {showPinBadge && (
            <div className="t-text-purple-30">
              <Pin />
            </div>
          )}
          {isAdmin && (
            <div
              className={classNames("t-group group-hover:t-block", {
                "t-block": showDropdown,
                "t-hidden": !showDropdown,
              })}
              onClick={stopPropagation}
            >
              <MarkRepliedUnreplied
                isFrozen={channel.data?.frozen as any as boolean}
                showDropdown={showDropdown}
                setShowDropdown={setShowDropdown}
                unreplied={unreplied}
                channelId={channel.id!}
              />
            </div>
          )}
        </div>
      </div>
      {Boolean(channel.data?.frozen) && (
        <div className="t-absolute t-inset-0 t-bg-white t-opacity-40" />
      )}
    </button>
  );
};
